import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useStyles } from '../style'
import { OutlinedButton } from '../../../elements'
import { TextField, Box, MenuItem, Grid } from '@material-ui/core'
import { useFormik } from 'formik'
import * as yup from 'yup'
import _ from 'lodash'
import { reportService } from '../../../Services/ReportService/ReportService'
import { DisplayMessage, Loader } from '../../../Components'
import * as messageTypes from '../../../utils/messageTypes'
import { pushToDataLayer, USERBEHAVIOURTRACKING } from '../../../utils/gaEnvironment'

const validationSchema = yup.object({
  account: yup
    .string('Select account name')
    .required('Account name is required'),
  program: yup
    .string('Select program name')
    .required('Program name is required'),
  site: yup
    .string('Enter site id')
    .matches(/(^[0-9]*)$/, 'Enter valid site id')
    .min(2, 'Site Id must be min 2 characters')
    .max(10, 'Site Id must be max 10 characters')
    .test('maxval', 'Invalid Site ID', val => Number(val) < Math.pow(2, 31))
    .required('Site Id is required'),
})

function EventParticipationReport(props) {
  const classes = useStyles()
  const [programs, setPrograms] = useState((props.isSuperAdmin || props.isViewOnly) ? props.programs : props.programs.filter(program => program.feature_flags && program.feature_flags.system_reports))
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [loading, setLoading] = useState(false)
  const { currentUser } = props

  const formik = useFormik({
    initialValues: {
      account: (props.isSuperAdmin || props.isViewOnly) ? '' : currentUser && currentUser.account_id,
      program: '',
      site: '',
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      pushToDataLayer({ event: USERBEHAVIOURTRACKING, accountID: currentUser.account_id, programID: values.program, siteID: values.site })
      getReport(values)
    },
  })

  const getReport = (values) => {
    setLoading(true)
    reportService.getEventParticipationHistoryReport(currentUser, values)
      .then(reportData => {
        setLoading(false)
        setOpen(true)
        if (values.report_type === 'PDF') {
          let popUp = window.open(reportData, '_blank', '')
          if (popUp == null || typeof (popUp) == 'undefined') {
            setMessage('Please disable your pop-up blocker and download file again.')
            setMessageType(messageTypes.INFO)
          }
          else {
            setMessage('Report downloading')
            setMessageType(messageTypes.SUCCESS)
          }
        } else {
          downloadFile(reportData, values)
          setMessage('Report downloading')
          setMessageType(messageTypes.SUCCESS)
        }
      })
      .catch(error => {
        setLoading(false)
        setOpen(true)
        setMessage(_.get(error, 'data.error.displayMessage', 'Something went wrong'))
        setMessageType(messageTypes.ERROR)
      })
  }

  const downloadFile = (fileUrl, values) => {
    const a = document.createElement("a")
    a.href = fileUrl
    a.target = values.report_type === 'PDF' ? "_blank" : ''
    a.setAttribute("download", `ProductionForecastReport_${new Date()}.${formik.values.report_type.toLowerCase()}`)
    a.click()
  }

  const handleAccountChange = (e) => {
    formik.setFieldValue([e.target.name], e.target.value)
    formik.setFieldValue('program', '')
    setPrograms(props.programs.filter(program => (props.isSuperAdmin || props.isViewOnly) ? program.account_id === e.target.value : program.account_id === e.target.value && program.feature_flags && program.feature_flags.system_reports))
  }

  useEffect(() => {
    formik.resetForm()
  }, [props.name])

  return (
    <>
      <Loader loading={loading} />
      <DisplayMessage open={open} message={message} messageType={messageType} handleClose={() => setOpen(false)} />
      <div className={classes.formHeading}>{props.name}</div>
      <div className={classes.formSubHeading}>Please enter the details below to download the report.</div>
      <form onSubmit={formik.handleSubmit}>
        <Box my={5}>
          <Grid container>
            <Grid item xs={12}>
              <Box display={(props.isSuperAdmin || props.isViewOnly) ? '' : 'none'}>
                <TextField
                  label="Account Name"
                  select
                  name="account"
                  fullWidth
                  value={formik.values.account}
                  disabled={!(props.isSuperAdmin || props.isViewOnly)}
                  style={{ maxWidth: 500 }}
                  onChange={handleAccountChange}
                  InputProps={{
                    classes: { root: classes.input }
                  }}
                  SelectProps={{
                    MenuProps: {
                      classes: { paper: classes.paper },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                      },
                      getContentAnchorEl: null
                    }
                  }}
                  error={formik.touched.account && Boolean(formik.errors.account)}
                  helperText={formik.touched.account && formik.errors.account}
                >
                  {(props.isSuperAdmin || props.isViewOnly) ? props.accounts.map(account => (
                    <MenuItem value={_.get(account, 'id', '')}>{_.get(account, 'company_name', 'NA')}</MenuItem>
                  )) :
                    <MenuItem value={currentUser.account_id}>{currentUser.account_company_name}</MenuItem>
                  }
                </TextField>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Program Name"
                select
                name="program"
                fullWidth
                value={formik.values.program}
                disabled={!formik.values.account}
                style={{ maxWidth: 500 }}
                onChange={formik.handleChange}
                InputProps={{
                  classes: { root: classes.input }
                }}
                SelectProps={{
                  MenuProps: {
                    classes: { paper: classes.paper },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    getContentAnchorEl: null
                  }
                }}
                error={formik.touched.program && Boolean(formik.errors.program)}
                helperText={formik.touched.program && formik.errors.program}
              >
                {programs.length > 0 ? programs.map(program => (
                  <MenuItem value={program.id}>{program.program_name_admin_ui}</MenuItem>
                )) :
                  <MenuItem value="noProgramFound" disabled>No Program Found</MenuItem>
                }
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Site Id"
                name="site"
                fullWidth
                value={formik.values.site}
                style={{ maxWidth: 500 }}
                onInput={(e) => {
                  e.target.value = e.target.value.toString().replace(/[^0-9]*/g, '')
                }}
                onChange={formik.handleChange}
                error={formik.touched.site && Boolean(formik.errors.site)}
                helperText={formik.touched.site && formik.errors.site}
              />
            </Grid>
          </Grid>
        </Box>
        <div>
          {<span className={classes.mr2} ><OutlinedButton type="submit" label="Download CSV" onClick={() => { formik.setFieldValue('report_type', 'CSV') }} /></span>}
          <span className={classes.mr2} ><OutlinedButton type="submit" label="Download PDF" onClick={() => { formik.setFieldValue('report_type', 'PDF') }} /></span>
        </div>
      </form>
    </>
  )
}

const mapStateToProps = ({ auth }) => {
  const { currentUser, isSuperAdmin, isViewOnly, timezone } = auth
  return { currentUser, isSuperAdmin, isViewOnly, timezone }
}

export default connect(mapStateToProps, {})(EventParticipationReport)